import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';

/**
 *  IGAZOLÁSOK ELŐZMÉNYE EGY TANULÓRA (backend/hianyzas-igazolas.js › GET /elozmeny)
 *
 *  Egy tanév minden igazolási nyoma időrendben, forrással és rögzítővel: az órák mostani
 *  igazolása (napra és típusra összevonva), az igazolás módosításai az óra naplójából, az
 *  osztályfőnöki napi igazolás, a gondviselő kérelmei, a KRÉTA e-Ügyintézés (EESZT) kérelmei és az
 *  igazolás átvitele a KRÉTA-naplóba. Csak olvas; a jogot a kiszolgáló dönti el (admin, vagy a
 *  tanuló osztályfőnöke / helyettese). Az osztályfőnöki oldal ion-modaljában nyílik.
 */

export interface ElozmenyTetel {
  forras: string; forras_nev: string; datum: string | null; ido: string | null;
  rogzito: string | null; rogzito_nev: string | null; tipus: number | null; tipus_nev: string;
  statusz: string | null; orak: { ora: number | null; tantargy: string }[]; hivatkozas: string | null; megjegyzes: string | null;
}

@Component({
  selector: 'app-of-igazolas-elozmeny',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './of-igazolas-elozmeny.component.html',
  styleUrls: ['./of-igazolas-elozmeny.component.scss'],
})
export class OfIgazolasElozmenyComponent implements OnChanges {
  @Input() intezmeny = '';
  @Input() oktazon = '';
  @Input() nev = '';
  @Input() tanev: number | null = null;
  @Output() bezar = new EventEmitter<void>();

  betolt = false;
  hiba = '';
  tetelek: ElozmenyTetel[] = [];
  forrasok: { kod: string; nev: string; db: number }[] = [];
  rejtett = new Set<string>();
  tanevKezd = '';
  tanevVege = '';

  constructor(private http: HttpClient) {}

  ngOnChanges(): void { this.betoltes(); }

  betoltes(): void {
    if (!this.intezmeny || !this.oktazon) { this.tetelek = []; return; }
    this.betolt = true;
    this.hiba = '';
    const ev = this.tanev ? `?ev=${this.tanev}` : '';
    this.http.get<any>(`/api/hianyzas-igazolas/elozmeny/${encodeURIComponent(this.oktazon)}/${encodeURI(this.intezmeny)}${ev}`).subscribe({
      next: (r) => {
        this.tetelek = Array.isArray(r?.tetelek) ? r.tetelek : [];
        this.forrasok = Array.isArray(r?.forrasok) ? r.forrasok : [];
        this.tanevKezd = String(r?.tanev_kezd || '');
        this.tanevVege = String(r?.tanev_vege || '');
        if (!this.nev && r?.nev) this.nev = r.nev;
        this.betolt = false;
      },
      error: (e) => {
        this.tetelek = [];
        this.betolt = false;
        this.hiba = e?.error?.error || 'Nem sikerült betölteni az igazolások előzményét.';
      },
    });
  }

  forrasValt(kod: string): void {
    if (this.rejtett.has(kod)) this.rejtett.delete(kod); else this.rejtett.add(kod);
    this.rejtett = new Set(this.rejtett);
  }

  get lathato(): ElozmenyTetel[] {
    return this.tetelek.filter((t) => !this.rejtett.has(t.forras));
  }

  /** A napok szerint csoportosítva (a lista már időrendben jön). */
  get napok(): { datum: string; tetelek: ElozmenyTetel[] }[] {
    const m = new Map<string, ElozmenyTetel[]>();
    for (const t of this.lathato) {
      const k = t.datum || '';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return [...m.entries()].map(([datum, tetelek]) => ({ datum, tetelek }));
  }

  orakSzoveg(t: ElozmenyTetel): string {
    if (!t.orak?.length) return '';
    const sav = t.orak.map((o) => (o.ora == null ? '?' : `${o.ora}.`)).join(', ');
    return `${t.orak.length} óra (${sav})`;
  }

  orakCim(t: ElozmenyTetel): string {
    return (t.orak || []).map((o) => `${o.ora == null ? '?' : o.ora + '.'} óra – ${o.tantargy || 'tantárgy nélkül'}`).join('\n');
  }

  tipusSzin(t: ElozmenyTetel): string {
    const n = Number(t.tipus);
    if (t.tipus == null || !Number.isFinite(n)) return 'ie-semleges';
    if (n <= -2) return 'ie-igazolatlan';
    if (n === -1) return 'ie-igazolando';
    return 'ie-igazolt';
  }

  pontos(iso: string | null): string {
    return /^\d{4}-\d{2}-\d{2}/.test(iso || '') ? `${iso!.slice(0, 4)}.${iso!.slice(5, 7)}.${iso!.slice(8, 10)}.${iso!.length > 10 ? iso!.slice(10) : ''}` : '';
  }
}
